import * as React from "react";
import { TableCell, TableRow } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { KeyNumberValue, KeyValue, Query } from "../../../interfaces";
import { ApiBaseUrl } from "../../../config";
import { fetchData } from "../../../common/services/ApiClient";
import { useHistory, useLocation } from "react-router-dom";
import generateBranchOfficeDepositsApiUrl from "./BranchOfficeDepositsUtilities";

interface Props {
  appState: Query;
  countyName: string;
  stateName: string;
  stateIndex: number;
  isBHC?: boolean;
}
const useStyles = makeStyles(theme => ({
  rowIndent: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 70,
    },
  },
  tableRow: {
    "cursor": "pointer",
    "&.MuiTableRow-root:hover, &.MuiTableRow-root:focus-visible": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#C0DEDE",
      fontWeight: "bold",
    },
  },
}));

export default function SODInstituteList({
  appState,
  countyName,
  stateName,
  stateIndex,
  isBHC,
}: Props) {
  const classes = useStyles();
  const [institutionData, setInstitutionData] = React.useState<KeyValue[]>([
    {},
  ]);
  const initialCountBy: KeyValue = {};
  const [
    countOfBranchOfficesByInstitution,
    setCountOfBranchOfficesByInstitution,
  ] = React.useState(initialCountBy);
  const [
    depositSummaryByInstitution,
    setDepositSummaryByInstitution,
  ] = React.useState(initialCountBy);
  const key = isBHC ? "RSSDHCR" : "CERT";
  const name = isBHC ? "NAMEHCR" : "NAMEFULL";

  const history = useHistory();
  const location = useLocation();
  const handleMouseClick = (data: KeyValue, event: KeyValue) => {
    appState.ui.institutionType === "banks"
      ? history.push({
          search: `&cert=${data.CERT}`,
          state: {
            appState: appState,
            backResults: `${location.pathname}${location.search}`,
          },
        })
      : history.push({
          search: `&bhcRSSD=${data.RSSDHCR}`,
          state: {
            appState: appState,
            backResults: `${location.pathname}${location.search}`,
          },
        });
  };

  React.useEffect(() => {
    let isMounted = true;
    const marketSelection = appState.filters.stateNames[0]
      ? appState.ui.marketSelection[stateIndex]
      : undefined;
    const bhcFilter = isBHC ? "RSSDHCR:>0" : "";

    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/sod`,
        generateBranchOfficeDepositsApiUrl(
          {
            ...appState,
            filters: {
              ...appState.filters,
              county: countyName,
              stalpbr: stateName,
            },
          },
          undefined,
          undefined,
          marketSelection,
          key,
          bhcFilter,
          name,
          "asc",
        ),
      )
        .then(response => {
          if (isMounted) {
            const filteredArray = response.data.filter(
              (obj: KeyValue) => obj.RSSDHCR !== 0,
            );
            setInstitutionData(isBHC ? filteredArray : response.data);
          }
        })
        .catch((error: Error) => {
          console.error(error);
        })
        .finally(() => (isMounted = false));
    })();
  }, [appState, countyName, isBHC, key, name, stateIndex, stateName]);

  React.useEffect(() => {
    let isMounted = true;
    const countOfBranchOfficesByInstitutionObj: KeyNumberValue = {};
    const depositSummaryByInstitutionObj: KeyNumberValue = {};
    const marketSelection = appState.filters.stateNames[0]
      ? appState.ui.marketSelection[stateIndex]
      : undefined;
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/sod`,
        generateBranchOfficeDepositsApiUrl(
          {
            ...appState,
            filters: {
              ...appState.filters,
              county: countyName,
              stalpbr: stateName,
            },
          },
          { by: key, limit: 10000, sum: ["DEPSUMBR"] },
          undefined,
          marketSelection,
          key,
        ),
      )
        .then(response => {
          if (isMounted) {
            for (const data of response.data) {
              const branchOfficesByInstit = data.count;
              const depSumByInstit = data.sum_DEPSUMBR;
              const id: number = data[key];
              countOfBranchOfficesByInstitutionObj[id] = branchOfficesByInstit;
              depositSummaryByInstitutionObj[id] = depSumByInstit;
            }
            setCountOfBranchOfficesByInstitution(
              countOfBranchOfficesByInstitutionObj,
            );
            setDepositSummaryByInstitution(depositSummaryByInstitutionObj);
          }
        })
        .catch((error: Error) => {
          console.error(error);
        })
        .finally(() => (isMounted = false));
    })();
  }, [appState, countyName, isBHC, key, stateIndex, stateName]);

  const dataColumns = (data: KeyValue) => {
    return (
      <>
        <TableCell className={classes.rowIndent}>{data[name]}</TableCell>
        <TableCell />
        <TableCell>
          {data.CITY}, {data.STNAME}
        </TableCell>
        <TableCell />
        <TableCell>
          {countOfBranchOfficesByInstitution[data[key]]
            ? countOfBranchOfficesByInstitution[data[key]].toLocaleString()
            : 0}
        </TableCell>
        <TableCell>{data[key]}</TableCell>
        {!isBHC && <TableCell>{data.BKCLASS}</TableCell>}
        <TableCell style={{ textAlign: "right" }}>
          {depositSummaryByInstitution[data[key]]
            ? depositSummaryByInstitution[data[key]].toLocaleString()
            : 0}
        </TableCell>
      </>
    );
  };

  return (
    <>
      {institutionData.map((data: KeyValue, index: number) => (
        <TableRow
          className={classes.tableRow}
          onClick={event => handleMouseClick(data, event)}
          key={index}
        >
          {dataColumns(data)}
        </TableRow>
      ))}
    </>
  );
}
